import ApplicationController from './application_controller';
import railsUploadAdapterPlugin from '../lib/ckeditor_upload_adapter';
import { loadBalloonEditor } from '../lib/ckeditor/load_balloon_editor';

// The blog post editor reuses Ambiki's self-hosted, commercially-licensed
// CKEditor 5 build (public/CKEditor5/ckeditor.js) — the same bundle the custom
// form editor uses. connect() loads it (loadBalloonEditor) when the page has
// not, and it exposes `BalloonEditor` and `ckeditorConfigLicenseKey` as
// globals.
//
// BalloonEditor has no persistent toolbar, so `buildToolbar` assembles one
// from the editor's component factory and pins it above the editable.

// Toolbar buttons, in display order; '|' renders a separator. Items the
// prebuilt bundle does not support are intentionally absent: SourceEditing,
// FindAndReplace, CodeBlock, RemoveFormat, WordCount, mediaEmbed (no
// component) and specialCharacters (its character sets are not compiled in,
// so the dropdown renders empty).
const TOOLBAR_ITEMS = [
  'undo',
  'redo',
  '|',
  'selectAll',
  '|',
  'heading',
  '|',
  'fontFamily',
  'fontSize',
  'fontColor',
  'fontBackgroundColor',
  'highlight',
  '|',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'subscript',
  'superscript',
  'code',
  '|',
  'link',
  'bulletedList',
  'numberedList',
  'blockQuote',
  '|',
  'alignment',
  'outdent',
  'indent',
  '|',
  'horizontalLine',
  '|',
  'uploadImage',
  'insertTable',
];

export default class extends ApplicationController {
  // `input` is the hidden <textarea> that carries the form value; `editor` is
  // the <div> BalloonEditor mounts on (it cannot mount on a <textarea>);
  // `toolbar` is the <div> the generated toolbar is placed into.
  static targets = ['input', 'editor', 'toolbar'];

  async connect() {
    let BalloonEditor;
    try {
      BalloonEditor = await loadBalloonEditor();
    } catch (error) {
      // The CKEditor build failed to load — fall back to the plain textarea.
      console.error('CKEditor build (public/CKEditor5/ckeditor.js) is not loaded.', error);
      this.inputTarget.classList.remove('hidden');
      return;
    }
    if (!this.element.isConnected) return;

    const editor = await BalloonEditor.create(this.editorTarget, {
      licenseKey: window.ckeditorConfigLicenseKey,
      placeholder: 'Your blog post...',
      // What is typed stays as typed: no 3/4 to ¾, no -> to an arrow (see shared/form/controls/_body).
      typing: { transformations: { remove: ['mathematical'] } },
      // We render our own persistent toolbar (buildToolbar); drop the floating
      // block toolbar (the "⠿" handle in the left margin). Base64UploadAdapter is
      // also removed so it cannot intercept pasted/dropped images and inline them
      // as base64 — railsUploadAdapterPlugin must own all uploads (to /blog/images).
      removePlugins: ['BlockToolbar', 'Base64UploadAdapter'],
      heading: {
        options: [
          { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
          { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
          { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
          { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
          { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
          { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
        ],
      },
      image: {
        toolbar: [
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
          '|',
          'toggleImageCaption',
          'imageTextAlternative',
          // 'linkImage' is intentionally absent: the prebuilt bundle does not
          // register that component, so listing it logs a toolbarview-item-unavailable warning.
        ],
      },
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableProperties',
          'tableCellProperties',
          'toggleTableCaption',
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
      },
      extraPlugins: [railsUploadAdapterPlugin],
    });

    this.editor = editor;

    // Stop the contextual balloon toolbar from popping up on selection — the
    // persistent toolbar built below replaces it.
    if (editor.plugins.has('BalloonToolbar')) {
      editor.plugins.get('BalloonToolbar').stopListening();
    }

    this.buildToolbar(editor);

    editor.setData(this.inputTarget.value || '');
    editor.model.document.on('change:data', () => {
      this.inputTarget.value = editor.getData();
    });

    this.onAutoSaveLoaded = this.onAutoSaveLoaded.bind(this);
    document.addEventListener('auto-save:loaded', this.onAutoSaveLoaded);
  }

  // Assemble a persistent toolbar from the editor's component factory. Each
  // button is a CKEditor UI view bound to its command, so clicks act on the
  // editor exactly as a native toolbar would.
  buildToolbar(editor) {
    this.toolbarViews = [];

    TOOLBAR_ITEMS.forEach((name) => {
      if (name === '|') {
        const separator = document.createElement('span');
        separator.className = 'blog-post-editor-toolbar-separator';
        this.toolbarTarget.appendChild(separator);
        return;
      }

      let view;
      try {
        view = editor.ui.componentFactory.create(name);
      } catch {
        // Item not registered in this build — skip it rather than crash.
        return;
      }

      view.render();
      this.toolbarViews.push(view);
      this.toolbarTarget.appendChild(view.element);
    });

    // Keep the editor "focused" while the toolbar is in use.
    editor.ui.focusTracker.add(this.toolbarTarget);
  }

  disconnect() {
    document.removeEventListener('auto-save:loaded', this.onAutoSaveLoaded || (() => {}));
    (this.toolbarViews || []).forEach((view) => view.destroy());
    this.toolbarViews = [];
    if (this.editor) {
      this.editor.destroy().catch(() => {});
      this.editor = null;
    }
  }

  onAutoSaveLoaded() {
    if (!this.editor) return;
    this.editor.setData(this.inputTarget.value || '');
  }
}
